import { useState, useEffect } from 'react';

const EXTENSION_SIGNATURE = 'MIIEowIBAAKCAQEAh7grzdLGLQLOC0JhbCM6b7/TePjBkpcpdLez9GhKvBFoyGxcFQy3BuIUrr+gd3EUJsW/SmCE+fA9FKVPxpJAYsNIksjKxlnf0mNDikjccO23K01nInPqNSaDnsLEerWvGFwoIf8dDMyMWN0fAf/J6Dm9OzRxzYJnP4s41jpB9NRd5JgLgUuhWty1u32yBkCXUNfNmMfiU9Kuj0eiZBbki4/uE0IQfJ5DjPA2mJrY9TFpU3tSevtmJXLNeaRKB7N4TP0bA6G2hspP7pEFt5OajH2JJUoF8gPr0wVApXyrfOV0ZlaXao7H5Sbnt6+fnQ00byCRB36o8rsgzwLAJj3fEwIDAQABAoIBAEKbFQxO66k9XidSbYNnio87Qip1UnKBsvuwCCXjZQIFPTpXS52c/tnm7CWSr8rOeQhKNgXndns+9osCUjNupSCTHDPFTRapFtomkwfzmBPn5ePfecUDxCZPf3rTtR9PuN2opJ2CmRwl6p7V7hjKEqngZEkBBPTJkagyyDMCpknjjpXQMqlJ1/k7XV+OCYmSXiMm4JngNQ1G1qJHVWKtzFa689/Y1af6xcmeKOKs/IJfaZMJoM7OztOm+6AdNsEmwoOm3/WsS5+kigeV3FgI4j2VOFuXzpCMgnD8WyZiPAb0DV9oXMo4y38D0Fi9afeTSs5BbrWaY96W6CxdTFkSc6kCgYEA7r+wekaHwJB/X0HxIkk9XX64YZ0m2geAfKgzEVteskchqL7FagRsGbjynONQo2sAeSiXzm7N+y8u/GPcd3s9NK9djBKXf/LjXeIYwrFeYhjsOGHBXYLOnVrM+EXtON0zv0UX+D4fOyJv8XC2K7s8VLjXY81MjfTh9gstqVpvSXUCgYEAkYasGzwVGCPTgcx7vAUYHSF6eNTaIlZuMm9cxwPWwrIFrcJ7IEI2JRzsbYjUUfXNu15uvGObbaoeSSMEtp20vZVJYF/XaeB+o7HgSNXiXEHfgnjbXQgZ0IwMaLsE90I5w46DnugA7nr5QOKBDWNk0DHYt8DgfEOnjsHpD8Xe7WcCgYEAwSvz/FtIx3Oiu049boJX1mgLg1+F6e8b9MCa4TZOfmyzPTdtv4Poez97fZkmy7Jt5ZoEihB5NP1ORQP/g+UA9rnVNNpuHiZF9NZVNmIQfcqwhkYCEEkgJ40qPs9FSzcfAWlEJaUN2+jXLkEGBIgwHlSYYbXgdRNoKI2O2nHDjWkCgYBdbsa/MaMlfZMFaiEjRW4G/gCg5aArnaxgq0k46YkQNbYZqpzcyEX1LA3MAaa3/IYbP3T/dUBMd4W8VURobvalJd0XiFFIw/7q4IBsow4TR6PWI/xLTbEtSU2KTFDS420s1ipBcxluA/xWIVbUHrWSSf6nc6ltfw5sRKl7HAKWDwKBgHmOFPLhpEHe8HjLzclyeMtjgup6hqTYljUosbvnRHWK+B3C9PhkEKpgP+3L0c0GPCo8CG8hQ22uXr2pNNhrr15IVTZlgrAxIW2s4WDl4ZO9iOUA7Qg98uFdyLcBhLjrHkoIou5TX1/zX0hzwq7Af7Z1J5S8zeSZjyW95FaMOOIQ';

interface ExtensionDetectorResult {
  isDetected: boolean;
  isChecking: boolean;
  detectionKey: string | null;
  isValidKey: boolean;
}

const useMeetExtensionDetection = (): ExtensionDetectorResult => {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [detectionKey, setDetectionKey] = useState<string | null>(null);

  useEffect(() => {
    const detectExtension = () => {
      // Method 1: Check for hidden div element
      const hiddenDiv = document.getElementById('meet-detect-key-holder');
      if (hiddenDiv) {
        const key = hiddenDiv.getAttribute('data-key');
        if (key) {
          setDetectionKey(key);
          setIsExtensionInstalled(true);
          setIsLoading(false);
          return true;
        }
      }

      // Method 2: Check document property
      if ((document as any).MeetDetectKey) {
        setDetectionKey((document as any).MeetDetectKey);
        setIsExtensionInstalled(true);
        setIsLoading(false);
        return true;
      }

      return false;
    };

    // Try immediate detection
    if (detectExtension()) {
      return;
    }

    // Listen for the custom event
    const handleExtensionDetected = (event: CustomEvent) => {
      if (event.detail && event.detail.key) {
        setDetectionKey(event.detail.key);
        setIsExtensionInstalled(true);
        setIsLoading(false);
      }
    };

    document.addEventListener('MeetDetectKeySet', handleExtensionDetected as EventListener);

    // Polling fallback with timeout
    let attempts = 0;
    const maxAttempts = 20; // 2 seconds total
    
    const pollForExtension = setInterval(() => {
      attempts++;
      
      if (detectExtension() || attempts >= maxAttempts) {
        clearInterval(pollForExtension);
        if (attempts >= maxAttempts && !isExtensionInstalled) {
          setIsLoading(false); // Extension not found
        }
      }
    }, 100);

    // Cleanup
    return () => {
      document.removeEventListener('MeetDetectKeySet', handleExtensionDetected as EventListener);
      clearInterval(pollForExtension);
    };
  }, [isExtensionInstalled]);

  return {
    isDetected: isExtensionInstalled,
    isChecking: isLoading,
    detectionKey,
    // Helper method to verify the key matches expected value
    isValidKey: detectionKey === EXTENSION_SIGNATURE,
  };
};

export default useMeetExtensionDetection;
